import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  AlertTitle,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import API from './Api';

// ── Category metadata ───────────────────────────────────────────────────────
const CATEGORY_META = {
  rent: {
    label: 'Rent',
    icon: <PaymentsIcon fontSize="small" />,
    color: '#1976d2',
    bg: '#e3f2fd',
    description: 'Monthly rent charges',
  },
  water: {
    label: 'Water',
    icon: <WaterDropIcon fontSize="small" />,
    color: '#0288d1',
    bg: '#e1f5fe',
    description: 'Water utility charges',
  },
  bills: {
    label: 'Bills',
    icon: <ElectricBoltIcon fontSize="small" />,
    color: '#f57c00',
    bg: '#fff3e0',
    description: 'Electricity & other bills',
  },
  penalties: {
    label: 'Penalties',
    icon: <WarningAmberIcon fontSize="small" />,
    color: '#d32f2f',
    bg: '#ffebee',
    description: 'Late payment penalties',
  },
  other: {
    label: 'Other',
    icon: <MoreHorizIcon fontSize="small" />,
    color: '#616161',
    bg: '#f5f5f5',
    description: 'Miscellaneous charges',
  },
};

const DEFAULT_ORDER = ['rent', 'water', 'bills', 'penalties', 'other'];

// ── Draggable category row ──────────────────────────────────────────────────
const CategoryRow = ({ category, index, total, onMoveUp, onMoveDown }) => {
  const meta = CATEGORY_META[category] || {
    label: category,
    icon: <MoreHorizIcon fontSize="small" />,
    color: '#616161',
    bg: '#f5f5f5',
    description: '',
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        mb: 1,
        border: '1.5px solid',
        borderColor: meta.color + '40',
        borderRadius: 2,
        bgcolor: meta.bg,
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: `0 2px 8px ${meta.color}30` },
      }}
    >
      {/* Priority badge */}
      <Box
        sx={{
          minWidth: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: meta.color,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 13,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Box>

      {/* Icon */}
      <Box sx={{ color: meta.color, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {meta.icon}
      </Box>

      {/* Label + description */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} color={meta.color} lineHeight={1.2}>
          {meta.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {meta.description}
        </Typography>
      </Box>

      {/* Move buttons */}
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        <Tooltip title="Move up">
          <span>
            <IconButton
              size="small"
              disabled={index === 0}
              onClick={() => onMoveUp(index)}
              sx={{ color: meta.color, '&:disabled': { opacity: 0.3 } }}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton
              size="small"
              disabled={index === total - 1}
              onClick={() => onMoveDown(index)}
              sx={{ color: meta.color, '&:disabled': { opacity: 0.3 } }}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 18, flexShrink: 0 }} />
    </Paper>
  );
};

// ── Main dialog ─────────────────────────────────────────────────────────────
/**
 * ManualProcessDialog
 *
 * Props:
 *   open            {boolean}   - controls dialog visibility
 *   onClose         {function}  - called when the dialog should close
 *   showNotification{function}  - (message, severity?) from parent
 *   currentOrder    {string[]}  - breakdown_settlement_order from company settings
 *                                 (may be empty; falls back to DEFAULT_ORDER)
 */
const ManualProcessDialog = ({ open, onClose, showNotification, currentOrder = [] }) => {
  const [processingCharges, setProcessingCharges] = useState(false);
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  // Initialise order when dialog opens or currentOrder changes
  useEffect(() => {
    if (open) {
      const base =
        currentOrder && currentOrder.length > 0 ? currentOrder : DEFAULT_ORDER;
      setOrder(base);
      setOrderDirty(false);
    }
  }, [open, currentOrder]);

  const moveUp = (index) => {
    if (index === 0) return;
    const next = [...order];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    setOrder(next);
    setOrderDirty(true);
  };

  const moveDown = (index) => {
    if (index === order.length - 1) return;
    const next = [...order];
    [next[index + 1], next[index]] = [next[index], next[index + 1]];
    setOrder(next);
    setOrderDirty(true);
  };

  const handleSaveOrder = async () => {
    try {
      setSavingOrder(true);
      // Fetch current payment schedule settings first so we don't clobber them
      const profileResp = await API.company.getCompanyProfile();
      const s = profileResp.company;

      await API.company.updatePaymentSchedule({
        payment_deduction_day: s.payment_deduction_day,
        grace_period_days: s.grace_period_days ?? 0,
        penalty_type: s.penalty_type || 'percentage',
        penalty_value: s.penalty_value ?? 0,
        breakdown_settlement_order: order,
      });

      setOrderDirty(false);
      showNotification('Settlement order saved successfully', 'success');
    } catch (err) {
      console.error(err);
      showNotification('Failed to save settlement order', 'error');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleProcess = async () => {
    try {
      setProcessingCharges(true);
      const response = await API.company.manualMonthlyProcessing({
        forceReprocess: false, // force is disabled
      });

      showNotification(
        `${response.message}  Processed: ${response.processedCount ?? 0}, Skipped: ${response.skippedCount ?? 0}`,
        'success'
      );
      onClose();
    } catch (err) {
      console.error(err);
      showNotification('Failed to process monthly charges', 'error');
    } finally {
      setProcessingCharges(false);
    }
  };

  const handleClose = () => {
    if (processingCharges || savingOrder) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
          bgcolor: '#1976d2',
          color: '#fff',
        }}
      >
        <CalendarMonthIcon />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Manual Monthly Processing
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            Process rent &amp; utility charges for all active tenants
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={processingCharges || savingOrder}
          sx={{ color: '#fff' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
        {/* ── Info banner ── */}
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>What happens when you process?</AlertTitle>
          Monthly rent and utility charges are applied to all active tenants who have not yet
          been processed this cycle. Tenants already processed this month will be
          <strong> automatically skipped</strong>.
        </Alert>

        {/* ── Force reprocess (disabled) ── */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
            mb: 3,
            border: '1.5px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: '#fafafa',
          }}
        >
          <LockIcon sx={{ color: 'text.disabled', mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              Force Reprocess — Disabled
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Force reprocessing (which re-charges tenants already processed this month) is
              not available through this interface. Please{' '}
              <strong>contact your service provider</strong> to enable this functionality.
            </Typography>
          </Box>
        </Paper>

        <Divider sx={{ mb: 3 }}>
          <Chip
            icon={<ReceiptLongIcon />}
            label="Payment Settlement Order"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Divider>

        {/* ── Settlement order explanation ── */}
        <Box
          sx={{
            p: 2,
            mb: 2.5,
            bgcolor: '#fffde7',
            border: '1px solid #ffe082',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="text.primary">
            <strong>How settlement order works:</strong> When a tenant makes a payment and
            carries a negative balance (arrears), incoming funds are applied against their
            outstanding charges <em>in the priority order shown below</em>. For example, if
            Rent is #1 and Water is #2, a partial payment clears rent arrears first before
            any remaining amount is applied to water.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Note: <strong>Initial / deposit</strong> allocations are always settled first
            and are not configurable here.
          </Typography>
        </Box>

        {/* ── Sortable order list ── */}
        <Box>
          {order.map((cat, idx) => (
            <CategoryRow
              key={cat}
              category={cat}
              index={idx}
              total={order.length}
              onMoveUp={moveUp}
              onMoveDown={moveDown}
            />
          ))}
        </Box>

        {orderDirty && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSaveOrder}
              disabled={savingOrder}
              startIcon={savingOrder ? <CircularProgress size={14} /> : null}
            >
              {savingOrder ? 'Saving order…' : 'Save Settlement Order'}
            </Button>
          </Box>
        )}

        {!orderDirty && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'right', mt: 1 }}
          >
            Use the arrows to reorder. Changes must be saved before processing.
          </Typography>
        )}
      </DialogContent>

      {/* ── Footer actions ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={processingCharges || savingOrder}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleProcess}
          variant="contained"
          color="warning"
          disabled={processingCharges || savingOrder || orderDirty}
          startIcon={processingCharges ? <CircularProgress size={18} color="inherit" /> : <CalendarMonthIcon />}
          sx={{ fontWeight: 700, minWidth: 180 }}
        >
          {processingCharges ? 'Processing…' : 'Process Charges'}
        </Button>
      </DialogActions>

      {orderDirty && (
        <Box
          sx={{
            px: 3,
            pb: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Typography variant="caption" color="warning.main">
            ⚠ Save the settlement order before processing.
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default ManualProcessDialog;